import { ChevronRightIcon } from '@heroicons/react/20/solid';
import { ArrowUpDown } from 'lucide-react';
import { useState } from 'react';

import { cn } from '../../../lib/utils';
import { DropdownDivider } from '../../ui/catalyst/dropdown';
import { SortOption } from './types';

/**
 * Props for SortingSection component
 */
interface SortingSectionProps {
  /** Current sort direction (null if not sorted) */
  sortDirection: 'asc' | 'desc' | null;
  /** Current sort field key (for multi-option sorting) */
  sortField?: string | null;
  /** Custom sort labels (optional) - used for single sort field */
  sortLabels?: {
    asc: string;
    desc: string;
  };
  /** Sort change handler - sortKey is provided for multi-option sorting */
  onSortChange: (direction: 'asc' | 'desc', sortKey?: string) => void;
  /**
   * Invert arrow direction for semantic display.
   * When true: ↑ shows desc label and triggers desc, ↓ shows asc label and triggers asc.
   */
  invertArrows?: boolean;
  /**
   * Multiple sort options for columns that can be sorted by different fields.
   * When provided, renders all options instead of simple asc/desc.
   */
  sortOptions?: SortOption[];
}

/**
 * Sorting section component for unified column filter dropdown
 *
 * Features:
 * - Expandable/collapsible sorting row
 * - Two sort options: Ascending and Descending
 * - Visual feedback for current sort direction
 * - Customizable labels
 * - Keyboard accessible
 *
 * Interaction:
 * 1. Click "Sorting" row to expand/collapse
 * 2. Select sort direction (closes automatically)
 * 3. Visual indicator shows active sort
 *
 * @example
 * ```tsx
 * <SortingSection
 *   sortDirection="asc"
 *   sortLabels={{ asc: 'A to Z', desc: 'Z to A' }}
 *   onSortChange={(dir) => console.log('Sort:', dir)}
 * />
 * ```
 */
export const SortingSection = ({
  sortDirection,
  sortField,
  sortLabels = { asc: 'A to Z', desc: 'Z to A' },
  onSortChange,
  invertArrows = false,
  sortOptions,
}: SortingSectionProps) => {
  // When inverted: ↑ shows desc, ↓ shows asc (semantic for dates: ↑ = newest)
  const topDirection = invertArrows ? 'desc' : 'asc';
  const bottomDirection = invertArrows ? 'asc' : 'desc';
  const topLabel = invertArrows ? sortLabels.desc : sortLabels.asc;
  const bottomLabel = invertArrows ? sortLabels.asc : sortLabels.desc;
  const [isExpanded, setIsExpanded] = useState(false);

  /**
   * Handle sort option selection (single sort field)
   * Closes the expanded section after selection
   */
  const handleSelectSort = (direction: 'asc' | 'desc') => {
    onSortChange(direction);
    setIsExpanded(false);
  };

  /**
   * Handle sort option selection (multi-option sorting)
   * Closes the expanded section after selection
   */
  const handleSelectMultiSort = (
    direction: 'asc' | 'desc',
    sortKey: string,
  ) => {
    onSortChange(direction, sortKey);
    setIsExpanded(false);
  };

  /**
   * Toggle expansion state
   */
  const handleToggleExpand = () => {
    setIsExpanded(!isExpanded);
  };

  /**
   * Handle keyboard interaction for toggle button
   */
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleToggleExpand();
    }
  };

  /**
   * Check if a specific sort option is currently active
   */
  const isOptionActive = (key: string, direction: 'asc' | 'desc') => {
    return sortField === key && sortDirection === direction;
  };

  return (
    <>
      {/* Sorting toggle row */}
      <div
        role="button"
        tabIndex={0}
        aria-expanded={isExpanded}
        aria-label="Sorting options"
        onClick={handleToggleExpand}
        onKeyDown={handleKeyDown}
        className="flex items-center justify-between px-3 py-2 hover:bg-gray-50 dark:hover:bg-zinc-800 cursor-pointer transition-colors"
      >
        <div className="flex items-center gap-2">
          <ArrowUpDown className="size-3.5 text-gray-500 dark:text-zinc-400" />
          <span className="text-xs font-medium text-black dark:text-white">
            Sorting
          </span>
        </div>
        <ChevronRightIcon
          className={cn(
            'size-3.5 transition-transform text-gray-600 dark:text-zinc-400',
            isExpanded && 'rotate-90',
          )}
          aria-hidden="true"
        />
      </div>

      {/* Expanded sort options */}
      {isExpanded && (
        <div className="px-3 py-1 bg-gray-50 dark:bg-zinc-900">
          {sortOptions && sortOptions.length > 0 ? (
            // Multi-option sorting: render all options
            sortOptions.map((option) => (
              <div key={option.key} className="mb-1 last:mb-0">
                {/* Ascending option */}
                <button
                  type="button"
                  onClick={() => handleSelectMultiSort('asc', option.key)}
                  className={cn(
                    'w-full flex items-center gap-2 px-2 py-1.5 rounded text-xs transition-colors hover:bg-gray-100 dark:hover:bg-zinc-800',
                    isOptionActive(option.key, 'asc')
                      ? 'text-blue-600 dark:text-blue-400'
                      : 'text-zinc-700 dark:text-zinc-300',
                  )}
                  aria-current={
                    isOptionActive(option.key, 'asc') ? 'true' : undefined
                  }
                >
                  <span aria-hidden="true">↑</span>
                  <span>{option.ascLabel}</span>
                </button>
                {/* Descending option */}
                <button
                  type="button"
                  onClick={() => handleSelectMultiSort('desc', option.key)}
                  className={cn(
                    'w-full flex items-center gap-2 px-2 py-1.5 rounded text-xs transition-colors hover:bg-gray-100 dark:hover:bg-zinc-800',
                    isOptionActive(option.key, 'desc')
                      ? 'text-blue-600 dark:text-blue-400'
                      : 'text-zinc-700 dark:text-zinc-300',
                  )}
                  aria-current={
                    isOptionActive(option.key, 'desc') ? 'true' : undefined
                  }
                >
                  <span aria-hidden="true">↓</span>
                  <span>{option.descLabel}</span>
                </button>
              </div>
            ))
          ) : (
            // Single sort field: render asc/desc options
            <>
              {/* Top option (↑) */}
              <button
                type="button"
                onClick={() => handleSelectSort(topDirection)}
                className={cn(
                  'w-full flex items-center gap-2 px-2 py-1.5 rounded text-xs transition-colors hover:bg-gray-100 dark:hover:bg-zinc-800',
                  sortDirection === topDirection
                    ? 'text-blue-600 dark:text-blue-400'
                    : 'text-zinc-700 dark:text-zinc-300',
                )}
                aria-current={
                  sortDirection === topDirection ? 'true' : undefined
                }
              >
                <span aria-hidden="true">↑</span>
                <span>{topLabel}</span>
              </button>

              {/* Bottom option (↓) */}
              <button
                type="button"
                onClick={() => handleSelectSort(bottomDirection)}
                className={cn(
                  'w-full flex items-center gap-2 px-2 py-1.5 rounded text-xs transition-colors hover:bg-gray-100 dark:hover:bg-zinc-800',
                  sortDirection === bottomDirection
                    ? 'text-blue-600 dark:text-blue-400'
                    : 'text-zinc-700 dark:text-zinc-300',
                )}
                aria-current={
                  sortDirection === bottomDirection ? 'true' : undefined
                }
              >
                <span aria-hidden="true">↓</span>
                <span>{bottomLabel}</span>
              </button>
            </>
          )}
        </div>
      )}

      {/* Divider after sorting section */}
      <DropdownDivider />
    </>
  );
};
